// Developer-report generator (spec §9, §11). Derives the report's conditional
// content from the live calculation state: the automatic disclaimers, the
// missing-data-as-tasks list, and the executive summary. The on-screen report
// (ReportTab) renders the static sections directly from project fields and uses
// these helpers for the computed parts. Report copy is Hebrew (it's the product
// output for the developer), unlike chat text.
import { isFilled } from '../state/model';
import { fmt } from './rights-engine';
import { classTotals } from '../state/matrix';

// ---- automatic disclaimers (§9.3) -------------------------------------------
export function collectDisclaimers(project, scenario, fit) {
  const out = [];
  const es = project.existingState;

  if (fit && fit.rows.some((r) => !r.missing && r.doesntFit > 0)) {
    out.push({
      key: 'partial_program',
      title: 'פרוגרמה חלקית',
      text: 'לפי החישוב הנוכחי ניתן להכניס חלק מפרוגרמת היזם במסגרת הזכויות המוקנות. הרכיבים שלא נכנסו מסומנים להמשך בדיקה או למסלול תכנוני נוסף.',
    });
  }

  const noSurvey = es.builtAsRaw?.building_file_evidence
    || ['auto_extracted', 'estimated'].includes(es.summary.builtArea?.status);
  if (noSurvey) {
    out.push({
      key: 'no_interior_survey',
      title: 'אין מדידת פנים',
      text: 'החישוב מתבסס על נתוני היתרים ומסמכים קיימים ולא על מדידת פנים עדכנית. יש לאמת את הנתונים לפני קבלת החלטה תכנונית או כלכלית.',
    });
  }

  const hasPotential = project.planStack.some((p) =>
    ['potential_ceiling', 'influence_layer'].includes(p.role.value));
  if (hasPotential) {
    out.push({
      key: 'far_potential',
      title: 'פוטנציאל רח"ק',
      text: 'הרח"ק מצביע על פוטנציאל תכנוני בלבד ואינו בהכרח זכות מוקנית להיתר. מימושו עשוי לדרוש תכנית או אישור נוסף.',
    });
  }

  const hasTreeSurvey = es.siteElements.some((e) => e.type === 'trees');
  if (!hasTreeSurvey) {
    out.push({
      key: 'trees_not_surveyed',
      title: 'עצים לא נסקרו',
      text: 'לא הוזן סקר עצים. עצים קיימים עשויים להשפיע על אפשרות המימוש ויש לבדוק את הנושא מול סקר עצים ודרישות הרשות.',
    });
  }

  return out;
}

// ---- missing data as tasks (§11) --------------------------------------------
const INPUT_TASK = {
  parcelArea: { item: 'שטח מגרש', why: 'נדרש כמעט לכל חישוב שטחים וזכויות', how: 'מדידה / נסח טאבו', impact: 'שטח כולל, תכסית, יתרת זכויות' },
  farPct: { item: 'אחוזי בנייה', why: 'קובע את השטח הכולל המותר', how: 'תב"ע / מידע להיתר', impact: 'שטח כולל מותר ויתרת זכויות' },
  coveragePct: { item: 'תכסית מותרת', why: 'מגביל את טביעת הרגל הבנויה', how: 'תב"ע / הנחיות', impact: 'תכסית ומצב מוצע' },
  densityPerDunam: { item: 'צפיפות יח"ד', why: 'קובע את מספר היחידות המותר', how: 'תב"ע / מידע להיתר', impact: 'יח"ד מותר ויתרה' },
  maxHeight: { item: 'גובה מותר', why: 'מגביל את גובה הבניין', how: 'תב"ע', impact: 'מעטפת מצב מוצע' },
  maxStories: { item: 'קומות מותר', why: 'מגביל את מספר הקומות', how: 'תב"ע', impact: 'מעטפת מצב מוצע' },
  parkingRatio: { item: 'תקן חניה', why: 'קובע את מספר החניות הנדרש', how: 'הנחיות מרחביות', impact: 'חניות נדרשות' },
  existingArea: { item: 'שטח בנוי קיים', why: 'נדרש לחישוב יתרת הזכויות', how: 'מטריצת מצב קיים / מדידה', impact: 'יתרת זכויות' },
  existingUnits: { item: 'יח"ד קיימות', why: 'נדרש לחישוב יתרת היחידות', how: 'מצב קיים / היתרים', impact: 'יתרת יח"ד' },
};

export function collectMissingData(project, metrics) {
  const keys = new Set();
  for (const m of metrics) {
    if (m.status === 'missing') m.missingInputs.forEach((k) => keys.has(k) || keys.add(k));
  }
  const tasks = [...keys].filter((k) => INPUT_TASK[k]).map((k) => INPUT_TASK[k]);

  // Standing checks that aren't engine inputs.
  if (!project.existingState.siteElements.some((e) => e.type === 'trees')) {
    tasks.push({ item: 'סקר עצים', why: 'עצים עשויים להגביל מימוש', how: 'אגרונום / סקר עצים', impact: 'תכנון, פיתוח והיתכנות' });
  }
  if (!project.documents.some((d) => /נסח|טאבו/.test(`${d.name} ${d.kind || ''}`))) {
    tasks.push({ item: 'נסח טאבו', why: 'בודק בעלויות, הצמדות וזיקות', how: 'הפקת נסח רשמי', impact: 'זכויות קנייניות ומגבלות' });
  }
  return tasks;
}

// ---- executive summary -------------------------------------------------------
export function buildExecSummary(project, metrics, fit) {
  const get = (k) => metrics.find((m) => m.key === k);
  const parts = [];

  const name = project.project.name?.value || 'הנכס';
  const addr = isFilled(project.property.address) ? `, ${project.property.address.value}` : '';
  parts.push(`דוח זה מסכם את בדיקת הזכויות עבור ${name}${addr}`);

  const remArea = get('remainingArea');
  if (remArea?.status === 'ok' && remArea.value < 0) {
    parts.push(`המבנה הקיים ממצה את זכויות תכנית הבסיס וחורג מהן בכ-${fmt(Math.abs(remArea.value))} מ"ר — ככל הנראה מכוח תכניות מאוחרות (למשל תמ"א 38); נדרש חישוב רב-שכבתי לקביעת היתרה בפועל`);
  } else if (remArea?.status === 'ok') {
    parts.push(`יתרת זכויות מוערכת של כ-${fmt(remArea.value)} מ"ר מול תכנית הבסיס`);
  } else {
    parts.push('יתרת הזכויות טרם חושבה במלואה (חסרים נתוני זכויות או שטח מגרש)');
  }

  if (fit) {
    const over = fit.rows.filter((r) => !r.missing && r.doesntFit > 0);
    if (over.length) parts.push(`חלק מהפרוגרמה אינו נכנס במסגרת הזכויות המוקנות (${over.map((r) => r.label).join(', ')}) ודורש בדיקה נוספת`);
    else if (fit.proposedArea > 0) parts.push('הפרוגרמה הנבדקת נכנסת במסגרת הזכויות לפי הנתונים הנוכחיים');
  }

  parts.push('כל הנתונים ניתנים לאימות ולעדכון, וחלקם חולצו אוטומטית וטעונים אישור');
  return parts.join('. ') + '.';
}

// ---- existing-state numeric snapshot for the report -------------------------
export function existingSnapshot(project) {
  const ct = classTotals(project.existingState.matrix);
  const s = project.existingState.summary;
  const matrixArea = ct.main + ct.service;
  const builtArea = isFilled(s.builtArea) ? Number(s.builtArea.value) : null;
  return {
    builtArea,
    units: isFilled(s.units) ? s.units.value : null,
    matrixMain: ct.main,
    matrixService: ct.service,
    // Same fallback the engine uses: classified matrix when filled, else the
    // as-built summary. fromSummary tells the report to label it accordingly.
    existingArea: matrixArea > 0 ? matrixArea : (builtArea ?? 0),
    fromSummary: matrixArea === 0 && builtArea != null,
  };
}

// Fields whose sources disagree (status "סותר") — surfaced in the report's
// limitations section so a conflict can't hide in a work tab.
export function collectConflicts(project) {
  const out = [];
  const scan = (obj, tab, labels) => {
    for (const [key, label] of Object.entries(labels)) {
      const f = obj?.[key];
      if (f && f.status === 'conflict') out.push({ label, note: f.note || '', key, tab });
    }
  };
  scan(project.property, 'property', { parcelArea: 'שטח מגרש', gush: 'גוש', helka: 'חלקה', landUse: 'ייעוד' });
  scan(project.rights, 'rights', { farPct: 'אחוזי בנייה', maxStories: 'קומות מותר', maxHeight: 'גובה מותר' });
  scan(project.existingState?.summary, 'existing', { builtArea: 'שטח בנוי', units: 'יח"ד קיימות' });
  return out;
}
